import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import { COMMERCE_ERROR, isCommerceError, whatsappOrderUrl } from '@loop-coffee/commerce-contracts';

import { useCommerce } from '../../commerce/CommerceContext';
import { describePaymentStatus } from '../../commerce/payments/registry';
import { Alert, CheckoutShell, contactLink, panel } from './ui';

export const RETURN_POLL_INTERVAL_MS = 3000;
export const RETURN_MAX_WAIT_MS = 3 * 60 * 1000;

const orderLabel = (order) => (order?.display_id ? `#${order.display_id}` : '');

function NoOrder() {
    return (
        <div style={panel}>
            <p className="text-white">Não encontramos um pedido recente neste navegador.</p>
            <p className="text-white-50" style={{ fontSize: 14 }}>
                Se você acabou de pagar, fale com a gente pelo WhatsApp com o número do pedido e confirmamos para você.
            </p>
            <Link to="/" className="btn btn-primary btn-sm me-2">Voltar à loja</Link>
            <a href={contactLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline-light btn-sm">Falar no WhatsApp</a>
        </div>
    );
}

function Paid({ order }) {
    return (
        <div style={panel} role="status">
            <Icon.CheckCircle size={32} color="#BE1E2D" className="mb-3" />
            <h2 className="text-white h4" data-testid="payment-status" data-state="confirmed">Pagamento confirmado</h2>
            <p className="text-white">Pedido {orderLabel(order)} confirmado.</p>
            <p className="text-white-50">Guarde o número do pedido. Qualquer dúvida sobre a entrega, fale com a gente pelo WhatsApp.</p>
            <Link to="/" className="btn btn-outline-light btn-sm">Voltar à loja</Link>
        </div>
    );
}

function Pending({ order, checkoutUrl, polling, onRetry }) {
    return (
        <div style={panel} role="status">
            <h2 className="text-white h4" data-testid="payment-status" data-state="pending">Aguardando confirmação do pagamento</h2>
            <p className="text-white">Pedido {orderLabel(order)}</p>
            <p className="text-white-50">
                {polling
                    ? 'Estamos acompanhando o pagamento e esta página atualiza sozinha. Pix e cartão costumam confirmar em poucos minutos.'
                    : 'Ainda não recebemos a confirmação. Se você já pagou, verifique de novo em alguns minutos ou fale com a gente pelo WhatsApp.'}
            </p>
            <div className="d-flex flex-wrap gap-2">
                {checkoutUrl && <a href={checkoutUrl} className="btn btn-primary btn-sm">Voltar para o pagamento</a>}
                {!polling && (
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={onRetry}>Verificar novamente</button>
                )}
            </div>
        </div>
    );
}

function NotPaid({ order, status }) {
    const help = whatsappOrderUrl(`Olá! Preciso de ajuda com o pagamento do pedido ${orderLabel(order)} da Loop Coffee.`);
    return (
        <div style={panel} role="status">
            <h2 className="text-white h4" data-testid="payment-status" data-state={status.state}>{status.label}</h2>
            <p className="text-white">Pedido {orderLabel(order)}</p>
            <p className="text-white-50">
                {status.state === 'canceled'
                    ? 'O pagamento deste pedido foi cancelado ou expirou. Fale com a gente pelo WhatsApp para refazer o pedido.'
                    : 'Se precisar de ajuda com este pedido, fale com a gente pelo WhatsApp.'}
            </p>
            <a href={help} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm me-2">Falar no WhatsApp</a>
            <Link to="/" className="btn btn-outline-light btn-sm">Voltar à loja</Link>
        </div>
    );
}

/**
 * Where the hosted payment page sends the customer back (paid, canceled or expired alike). The
 * return URL carries nothing trustworthy, so the page ignores it and follows the order stored at
 * checkout, showing only what the order's `payment_status` says.
 */
export default function PaymentReturn({ pollIntervalMs = RETURN_POLL_INTERVAL_MS, maxWaitMs = RETURN_MAX_WAIT_MS }) {
    const { enabled, client, pendingPayment } = useCommerce();
    const [stored] = useState(() => (enabled ? pendingPayment.get() : null));
    // status: loading | ready | missing
    const [state, setState] = useState({ status: stored ? 'loading' : 'missing', order: null, failed: false });
    const [polling, setPolling] = useState(Boolean(stored));
    const timer = useRef(null);
    const orderId = stored?.orderId;

    const startPolling = useCallback(() => setPolling(true), []);

    useEffect(() => {
        if (!polling || !orderId) return undefined;
        let active = true;
        let attempts = 0;
        const maxAttempts = Math.max(1, Math.ceil(maxWaitMs / pollIntervalMs));

        const check = async () => {
            attempts += 1;
            try {
                const order = await client.getOrder(orderId);
                if (!active) return;
                setState({ status: 'ready', order, failed: false });
                if (describePaymentStatus(order.payment_status).state !== 'pending') {
                    setPolling(false);
                    return;
                }
            } catch (e) {
                if (!active) return;
                if (isCommerceError(e, COMMERCE_ERROR.NOT_FOUND)) {
                    setState({ status: 'missing', order: null, failed: false });
                    setPolling(false);
                    return;
                }
                setState((prev) => ({ ...prev, failed: true }));
            }
            if (attempts >= maxAttempts) {
                setPolling(false);
                return;
            }
            timer.current = setTimeout(check, pollIntervalMs);
        };

        check();
        return () => {
            active = false;
            clearTimeout(timer.current);
        };
    }, [polling, orderId, client, pollIntervalMs, maxWaitMs]);

    let content;
    if (!enabled || state.status === 'missing') {
        content = <NoOrder />;
    } else if (state.status === 'loading') {
        content = !state.failed
            ? <p role="status" className="text-white-50">Consultando seu pedido…</p>
            : (
                <Alert>
                    Não foi possível consultar seu pedido agora.{polling ? ' Tentaremos de novo em instantes.' : ''}
                    {!polling && (
                        <div className="mt-2">
                            <button type="button" className="btn btn-sm btn-outline-light" onClick={startPolling}>Verificar novamente</button>
                        </div>
                    )}
                </Alert>
            );
    } else {
        const status = describePaymentStatus(state.order.payment_status);
        if (status.state === 'confirmed') {
            content = <Paid order={state.order} />;
        } else if (status.state === 'pending') {
            content = (
                <>
                    {state.failed && <Alert>Não foi possível atualizar o status agora. Tentaremos de novo.</Alert>}
                    <Pending order={state.order} checkoutUrl={stored.checkoutUrl} polling={polling} onRetry={startPolling} />
                </>
            );
        } else {
            content = <NotPaid order={state.order} status={status} />;
        }
    }

    return <CheckoutShell title="Pagamento">{content}</CheckoutShell>;
}
